import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  LoginResponse,
  ProfileUpdatePayload,
  ChangePasswordPayload,
  ClinicUpdatePayload,
  ProfileClinicUpdatePayload,
} from '../../api';

interface UserData {
  id: string;
  email: string;
  title?: string;
  first_name: string;
  last_name: string;
  phone?: string;
  address?: string;
  dob?: string;
  user_role: string;
  communication_preference?: string;
  verified?: boolean;
  clinic?: {
    id: number;
    name: string;
    abn: string;
    address: string;
    specialty: string;
  } | null;
}

interface AuthState {
  user: UserData | null;
  loading: boolean;
  error: string | null;
  tokens: {
    access: string | null;
    refresh: string | null;
  };
  passwordChange: {
    loading: boolean;
    error: string | null;
    success: boolean;
  };
  profileUpdate: {
    loading: boolean;
    error: string | null;
    success: boolean;
  };
  emailVerification: {
    loading: boolean;
    error: string | null;
    success: boolean;
    email: string | null;
    password: string | null; // Temporarily store password for auto-login after verification
  };
  clinicSetup: {
    loading: boolean;
    error: string | null;
    success: boolean;
  };
}

const initialState: AuthState = {
  user: null,
  loading: false,
  error: null,
  tokens: {
    access: null,
    refresh: null,
  },
  passwordChange: {
    loading: false,
    error: null,
    success: false,
  },
  profileUpdate: {
    loading: false,
    error: null,
    success: false,
  },
  emailVerification: {
    loading: false,
    error: null,
    success: false,
    email: null,
    password: null,
  },
  clinicSetup: {
    loading: false,
    error: null,
    success: false,
  },
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginRequest: (state, action: PayloadAction<{ email: string; password: string }>) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (
      state,
      action: PayloadAction<{ user: UserData; tokens: { access: string; refresh: string } }>
    ) => {
      state.loading = false;
      state.user = action.payload.user;
      state.tokens = action.payload.tokens;
      state.error = null;
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    registerRequest: (
      state,
      action: PayloadAction<{
        first_name: string;
        last_name: string;
        email: string;
        password: string;
        confirm_password: string;
        country?: string;
      }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    registerSuccess: (
      state,
      action: PayloadAction<{ message: string; email: string; password: string }>
    ) => {
      state.loading = false;
      state.error = null;
      state.emailVerification.email = action.payload.email;
      state.emailVerification.password = action.payload.password;
    },
    registerFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    verifyEmailRequest: (state, action: PayloadAction<{ email: string; code: string }>) => {
      state.emailVerification.loading = true;
      state.emailVerification.error = null;
    },
    verifyEmailSuccess: (
      state,
      action: PayloadAction<{
        message: string;
        user: UserData;
        tokens: { access: string; refresh: string };
      }>
    ) => {
      state.emailVerification.loading = false;
      state.emailVerification.success = true;
      state.user = action.payload.user;
      state.tokens = action.payload.tokens;
      state.error = null;
    },
    verifyEmailFailure: (state, action: PayloadAction<string>) => {
      state.emailVerification.loading = false;
      state.emailVerification.error = action.payload;
    },
    clearEmailVerificationSuccess: state => {
      state.emailVerification.success = false;
      state.emailVerification.password = null; // Clear stored password for security
    },
    resendCodeRequest: (state, action: PayloadAction<{ email: string }>) => {
      state.emailVerification.loading = true;
      state.emailVerification.error = null;
    },
    resendCodeSuccess: (state, action: PayloadAction<{ message: string }>) => {
      state.emailVerification.loading = false;
      state.emailVerification.error = null;
    },
    resendCodeFailure: (state, action: PayloadAction<string>) => {
      state.emailVerification.loading = false;
      state.emailVerification.error = action.payload;
    },
    setupClinicRequest: (
      state,
      action: PayloadAction<{
        field_of_practice: string;
        business_name: string;
        practice_management_software: string;
        location: string;
        is_practice_manager: boolean;
      }>
    ) => {
      state.clinicSetup.loading = true;
      state.clinicSetup.error = null;
    },
    setupClinicSuccess: state => {
      state.clinicSetup.loading = false;
      state.clinicSetup.success = true;
      state.clinicSetup.error = null;
    },
    setupClinicFailure: (state, action: PayloadAction<string>) => {
      state.clinicSetup.loading = false;
      state.clinicSetup.error = action.payload;
    },
    googleLoginRequest: (state, action: PayloadAction<{ credential: string }>) => {
      state.loading = true;
      state.error = null;
    },
    googleLoginSuccess: (state, action: PayloadAction<LoginResponse>) => {
      state.loading = false;
      state.user = action.payload.user;
      state.tokens = { access: action.payload.access, refresh: action.payload.refresh };
      state.error = null;
    },
    googleLoginFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    logoutReduxState: state => {
      state.loading = false;
      state.user = null;
      state.tokens = { access: null, refresh: null };
      state.error = null;
    },
    refreshTokenRequest: state => {
      state.loading = true;
      state.error = null;
    },
    refreshTokenSuccess: (state, action: PayloadAction<{ access: string; refresh: string }>) => {
      state.loading = false;
      state.tokens = action.payload;
      state.error = null;
    },
    refreshTokenFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    updateAccessToken: (state, action: PayloadAction<string>) => {
      state.tokens.access = action.payload;
    },
    setTokens: (state, action: PayloadAction<{ access: string; refresh: string }>) => {
      state.tokens = action.payload;
    },
    activateUserRequest: state => {
      state.loading = true;
      state.error = null;
    },
    activateUserSuccess: state => {
      state.loading = false;
    },
    activateUserFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    changePasswordRequest: (state, action: PayloadAction<ChangePasswordPayload>) => {
      void action; // Suppress unused parameter warning
      // Ensure passwordChange object exists
      if (!state.passwordChange) {
        state.passwordChange = {
          loading: false,
          error: null,
          success: false,
        };
      }
      state.passwordChange.loading = true;
      state.passwordChange.error = null;
      state.passwordChange.success = false;
    },
    changePasswordSuccess: state => {
      // Ensure passwordChange object exists
      if (!state.passwordChange) {
        state.passwordChange = {
          loading: false,
          error: null,
          success: false,
        };
      }
      state.passwordChange.loading = false;
      state.passwordChange.error = null;
      state.passwordChange.success = true;
    },
    changePasswordFailure: (state, action: PayloadAction<string>) => {
      // Ensure passwordChange object exists
      if (!state.passwordChange) {
        state.passwordChange = {
          loading: false,
          error: null,
          success: false,
        };
      }
      state.passwordChange.loading = false;
      state.passwordChange.error = action.payload;
      state.passwordChange.success = false;
    },
    clearPasswordChangeState: state => {
      // Ensure passwordChange object exists
      if (!state.passwordChange) {
        state.passwordChange = {
          loading: false,
          error: null,
          success: false,
        };
      }
      state.passwordChange.loading = false;
      state.passwordChange.error = null;
      state.passwordChange.success = false;
    },

    // Profile update actions
    updateProfileRequest: (state, action: PayloadAction<ProfileUpdatePayload>) => {
      void action; // Suppress unused parameter warning
      // Ensure profileUpdate object exists
      if (!state.profileUpdate) {
        state.profileUpdate = {
          loading: false,
          error: null,
          success: false,
        };
      }
      state.profileUpdate.loading = true;
      state.profileUpdate.error = null;
      state.profileUpdate.success = false;
    },
    updateClinicRequest: (state, action: PayloadAction<ClinicUpdatePayload>) => {
      void action; // Suppress unused parameter warning
      // Ensure profileUpdate object exists
      if (!state.profileUpdate) {
        state.profileUpdate = {
          loading: false,
          error: null,
          success: false,
        };
      }
      state.profileUpdate.loading = true;
      state.profileUpdate.error = null;
      state.profileUpdate.success = false;
    },
    updateProfileAndClinicRequest: (state, action: PayloadAction<ProfileClinicUpdatePayload>) => {
      void action; // Suppress unused parameter warning
      // Ensure profileUpdate object exists
      if (!state.profileUpdate) {
        state.profileUpdate = {
          loading: false,
          error: null,
          success: false,
        };
      }
      state.profileUpdate.loading = true;
      state.profileUpdate.error = null;
      state.profileUpdate.success = false;
    },
    updateClinicSuccess: (state, action: PayloadAction<UserData>) => {
      // Ensure profileUpdate object exists
      if (!state.profileUpdate) {
        state.profileUpdate = {
          loading: false,
          error: null,
          success: false,
        };
      }
      state.profileUpdate.loading = false;
      state.profileUpdate.error = null;
      state.profileUpdate.success = true;
      state.user = action.payload;
    },
    updateClinicFailure: (state, action: PayloadAction<string>) => {
      // Ensure profileUpdate object exists
      if (!state.profileUpdate) {
        state.profileUpdate = {
          loading: false,
          error: null,
          success: false,
        };
      }
      state.profileUpdate.loading = false;
      state.profileUpdate.error = action.payload;
      state.profileUpdate.success = false;
    },
    updateProfileAndClinicSuccess: (state, action: PayloadAction<UserData>) => {
      // Ensure profileUpdate object exists
      if (!state.profileUpdate) {
        state.profileUpdate = {
          loading: false,
          error: null,
          success: false,
        };
      }
      state.profileUpdate.loading = false;
      state.profileUpdate.error = null;
      state.profileUpdate.success = true;
      state.user = action.payload;
    },
    updateProfileAndClinicFailure: (state, action: PayloadAction<string>) => {
      // Ensure profileUpdate object exists
      if (!state.profileUpdate) {
        state.profileUpdate = {
          loading: false,
          error: null,
          success: false,
        };
      }
      state.profileUpdate.loading = false;
      state.profileUpdate.error = action.payload;
      state.profileUpdate.success = false;
    },
    updateProfileSuccess: (state, action: PayloadAction<UserData>) => {
      // Ensure profileUpdate object exists
      if (!state.profileUpdate) {
        state.profileUpdate = {
          loading: false,
          error: null,
          success: false,
        };
      }
      state.profileUpdate.loading = false;
      state.profileUpdate.error = null;
      state.profileUpdate.success = true;
      state.user = action.payload;
    },
    updateProfileFailure: (state, action: PayloadAction<string>) => {
      // Ensure profileUpdate object exists
      if (!state.profileUpdate) {
        state.profileUpdate = {
          loading: false,
          error: null,
          success: false,
        };
      }
      state.profileUpdate.loading = false;
      state.profileUpdate.error = action.payload;
      state.profileUpdate.success = false;
    },
    clearProfileUpdateState: state => {
      // Ensure profileUpdate object exists
      if (!state.profileUpdate) {
        state.profileUpdate = {
          loading: false,
          error: null,
          success: false,
        };
      }
      state.profileUpdate.loading = false;
      state.profileUpdate.error = null;
      state.profileUpdate.success = false;
    },
  },
});

export const {
  loginRequest,
  loginSuccess,
  loginFailure,
  registerRequest,
  registerSuccess,
  registerFailure,
  verifyEmailRequest,
  verifyEmailSuccess,
  verifyEmailFailure,
  clearEmailVerificationSuccess,
  resendCodeRequest,
  resendCodeSuccess,
  resendCodeFailure,
  setupClinicRequest,
  setupClinicSuccess,
  setupClinicFailure,
  googleLoginRequest,
  googleLoginSuccess,
  googleLoginFailure,
  logoutReduxState,
  refreshTokenRequest,
  refreshTokenSuccess,
  refreshTokenFailure,
  updateAccessToken,
  setTokens,
  activateUserRequest,
  activateUserSuccess,
  activateUserFailure,
  changePasswordRequest,
  changePasswordSuccess,
  changePasswordFailure,
  clearPasswordChangeState,
  updateProfileRequest,
  updateProfileSuccess,
  updateProfileFailure,
  clearProfileUpdateState,
  updateClinicRequest,
  updateClinicSuccess,
  updateClinicFailure,
  updateProfileAndClinicRequest,
  updateProfileAndClinicSuccess,
  updateProfileAndClinicFailure,
} = authSlice.actions;

export default authSlice.reducer;
